import {createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode} from "react";
import {
 defaultLocale,
 localeFormatting,
 localizationCatalog,
 supportedLocales,
 type SupportedLocale,
} from "./generated/catalog.generated";

type Catalog=Record<string,unknown>;
type LocalizationContextValue={locale:SupportedLocale;setLocale(value:SupportedLocale):void;t(key:string):string};
const Ctx=createContext<LocalizationContextValue|null>(null);
const RTL_LANGUAGES=new Set(["ar","fa","he","ur"]);
const LOCALE_STORAGE_KEY="shifath-platform-admin-locale";

function lookup(root:Catalog,key:string):string{
 let value:unknown=root;
 for(const part of key.split(".")){
  if(!value||typeof value!=="object")return key;
  value=(value as Record<string,unknown>)[part];
 }
 return typeof value==="string"?value:key;
}

function localeDirection(locale:SupportedLocale):"ltr"|"rtl"{
 try{
  const textInfo=(new Intl.Locale(locale) as Intl.Locale&{textInfo?:{direction?:string}}).textInfo;
  if(textInfo?.direction==="rtl")return "rtl";
  if(textInfo?.direction==="ltr")return "ltr";
 }catch{}
 return RTL_LANGUAGES.has(locale.toLowerCase().split("-")[0])?"rtl":"ltr";
}

export function LocalizationProvider({children}:{children:ReactNode}){
 const[locale,setLocale]=useState<SupportedLocale>(()=>{
  try{
   const saved=window.localStorage.getItem(LOCALE_STORAGE_KEY);
   if(saved&&supportedLocales.includes(saved as SupportedLocale))return saved as SupportedLocale;
  }catch{}
  return defaultLocale;
 });
 useLayoutEffect(()=>{
  const root=document.documentElement;
  const formatting=localeFormatting[locale];
  try{window.localStorage.setItem(LOCALE_STORAGE_KEY,locale)}catch{}
  root.lang=locale;
  root.dir=localeDirection(locale);
  root.dataset.locale=locale;
  document.title=`${lookup(localizationCatalog[locale] as Catalog,"core.common.app_name")} · ${lookup(localizationCatalog[locale] as Catalog,"core.common.surface_name")}`;
  root.style.setProperty("--locale-font-family",formatting.fontFamily);
  root.style.setProperty("--locale-text-color",formatting.textColor);
  root.style.setProperty("--locale-font-size",formatting.fontSize);
  root.style.setProperty("--locale-line-height",formatting.lineHeight);
  root.style.setProperty("--locale-letter-spacing",formatting.letterSpacing);
 },[locale]);
 const value=useMemo<LocalizationContextValue>(()=>({
  locale,
  setLocale,
  t:(key:string)=>lookup(localizationCatalog[locale] as Catalog,key),
 }),[locale]);
 return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocalization(){
 const value=useContext(Ctx);
 if(!value)throw new Error("LocalizationProvider missing");
 return value;
}
